import React, {useState} from 'react';
import {Link} from 'react-router';
import {
  Anchor,
  Button,
  ButtonGroup,
  Dialogue,
  Icon,
  Text,
  Modal,
  LoadingSpinner
} from '@albert-io/atomic';
import classnames from 'classnames';
import {useFluxStore} from 'client/framework';
import sessionStore from 'client/Session/SessionStore';
import {doesUserBelongToAnyLicensedClassrooms} from 'lib/UserAccessUtil';

import appStore from 'client/AppStore';

import benefitsImage from './ImageAssets/benefits.svg';
import homeImage from './ImageAssets/home.svg';
import chalkboardImage from './ImageAssets/chalkboard.svg';

import './upgrade-subject-modal.scss';

/*
Below are all of the cases of the modal state mapped out to the user types.
The object form was the easiest way to keep track of each state and is easy to refer back to
when editing the file or needing to change copy for a specific user scenario.

{
  unlicensedTeacherRetailSubject: {
    heading: 'Purchase a license to unlock the power of Albert for your whole school',
    image: 'stacked books',
    body: [
      'Access to all subjects, for all students and teachers at your school',
      'The ability to assign any question in any subject',
      'Powerful data reports at the school, class and student level'
    ],
    buttons: ['upgrade subject', 'learn about license']
  },
  unlicensedTeacherNonRetailSubject: {
    heading: 'Purchase a license to unlock the power of Albert for your whole school',
    image: 'stacked books',
    body: [
      'Access to all subjects, for all students and teachers at your school',
      'The ability to assign any question in any subject',
      'Powerful data reports at the school, class and student level'
    ],
    buttons: ['learn about license']
  },
  retailStudentRetailSubject: {
    heading: 'Upgrade now and get everything you need to prepare for',
    image: 'backpack',
    body: [
      'Access To {x more}/all practice questions',
      'Access To {x more}/all free response examples',
      'Detailed explanations for every question',
      'Unlimited attempts per question'
    ],
    buttons: ['Upgrade subject']
  },
  retailStudentNonRetailSubject: {
    heading: '[Subject] is only available to teachers',
    image: 'chalkboard',
    body: ['In order to complete this assessment, you must belong to a class. This is not available to individual subscribers.'],
    buttons: ['none']
  },
  licensedStudentViewingAssessment: {
    heading: 'Ask your teacher to grant you access to',
    image: 'backpack',
    body: ['You can access this assessment in the Assessments tab of your class! If you don&apos;t see it there, make sure your teacher has assigned it to you.'],
    buttons: ['none']
  },
  licensedStudentViewingAssignmentWithClosedIP: {
    heading: null,
    image: ['backpack'],
    body: ['Your teacher has restricted access to this subject'],
    buttons: ['none']
    subtext: ['Reach out to your teacher if you should have access to this subject.']
  }
  licensedStudentRetailSubject: {
    heading: 'Want to practice [Subject] at your own pace?',
    image: 'backpack',
    body: ['Ask your teacher to grant you independent practice access'],
    buttons: ['none']
  }
}
*/

interface SellingPointProps {
  total?: number;
  content: string;
}

function generateSellingPoint({total, content}: SellingPointProps) {
  if (total === 0) {
    return null;
  }

  return (
    <li className='upgrade-subject-modal__selling-point-wrapper'>
      <Icon size='standalone' icon='check' color='positive' />
      <Text color='secondary' className='upgrade-subject-modal__selling-point'>
        {content}
      </Text>
    </li>
  );
}

interface Props {
  /** Subject for the current active question set */
  subject?: any;
  /**
   * Modal close function. `propTypes` marked this required, but callers that render the
   * modal as a non-dismissable access notice omit it — hence the close-button guard below.
   */
  handleClose?: () => void;
}

export default function UpgradeSubjectModal({subject, handleClose}: Props) {
  'use memo';

  const isTeacher = useFluxStore(sessionStore, (store) => store.isTeacher());
  const isStudent = useFluxStore(sessionStore, (store) => store.isStudent());
  // `doesUserBelongToAnyLicensedClassrooms` defaults `includeTeacher` to false, so the
  // original's bare `()` and explicit `(false)` call sites were the same predicate.
  const belongsToLicensedClassrooms = useFluxStore(
    sessionStore,
    () => doesUserBelongToAnyLicensedClassrooms(false).value
  );
  const isLicensedClassroomsReady = useFluxStore(
    sessionStore,
    () => doesUserBelongToAnyLicensedClassrooms(false).isReady
  );
  const pathname = useFluxStore(
    appStore,
    (store) => (store.routerProps.location as {pathname: string}).pathname
  );

  // Snapshot of the subject taken on mount: the class computed this once in its
  // constructor and never resynced it, so a lazy initializer preserves that.
  const [isNotRetailSubject] = useState(() => !subject?.getSubscriptionsPrices().first());

  const isLicensedStudentViewingAssessment = () =>
    isStudent && belongsToLicensedClassrooms && pathname.includes('/assessments');

  const isLicensedStudentViewingClosedIPSubject = () =>
    isStudent && belongsToLicensedClassrooms && !subject.doesUserHaveAccess();

  const isLicensedStudentViewingFRQGuide = () =>
    isStudent &&
    belongsToLicensedClassrooms &&
    subject.doesUserHaveAccess() &&
    pathname.includes('/free-response');

  const generateCallToActionHeader = () => {
    if (isLicensedStudentViewingClosedIPSubject() || isLicensedStudentViewingFRQGuide()) {
      return null;
    }
    const titleString = 'Purchase a license to unlock the power of Albert for your whole school';
    if (!subject) {
      return (
        <Text bold as='div' className='upgrade-subject-modal__title' color='secondary'>
          {titleString}
        </Text>
      );
    }
    const subjectName = <Text>{subject.get('name')}</Text>;
    const isLicensed = belongsToLicensedClassrooms;
    if (isNotRetailSubject && !isTeacher && !isLicensed) {
      return (
        <Text bold as='div' className='upgrade-subject-modal__title' color='secondary'>
          {subjectName} is only available to teachers
        </Text>
      );
    }
    if (!isTeacher) {
      if (isLicensedStudentViewingAssessment()) {
        return null;
      }

      return isLicensed ? (
        <Text bold as='div' className='upgrade-subject-modal__title' color='secondary'>
          Want to practice {subjectName} at your own pace?
        </Text>
      ) : (
        <Text bold as='div' className='upgrade-subject-modal__title' color='secondary'>
          Upgrade now and get everything you need to prepare for {subjectName}
        </Text>
      );
    }
    return (
      <Text bold as='div' className='upgrade-subject-modal__title' color='secondary'>
        {titleString}
      </Text>
    );
  };

  const generateSellingPoints = () => {
    const questionsTotal = subject?.getMeta()?.getCountOfQuestions();
    const freeResponseTotal = subject?.getMeta()?.getCountOfFreeResponseQuestions();
    if (isTeacher || !subject) {
      return (
        <ul className='upgrade-subject-modal__selling-point-list'>
          {generateSellingPoint({
            content: `Access to all subjects, for all students and teachers at your school`
          })}
          {generateSellingPoint({
            content: `The ability to assign any question in any subject`
          })}
          {generateSellingPoint({
            content: `Powerful data reports at the school, class and student level`
          })}
          {generateSellingPoint({
            content: `Access to search results`
          })}
        </ul>
      );
    }
    if (isLicensedStudentViewingAssessment()) {
      return (
        <Text as='p' className='m-x0 text-center'>
          Assessment questions must be assigned by your teacher. You can view your assignments from
          the classes tab.
        </Text>
      );
    }
    if (isLicensedStudentViewingClosedIPSubject()) {
      return (
        <>
          <Text as='p' className='text-center'>
            Your teacher has restricted access to this subject.
          </Text>
          <Text size='s' as='p' className='mt-x4 text-center' color='secondary'>
            Reach out to your teacher if you should have access to this subject.
          </Text>
        </>
      );
    }
    if (isLicensedStudentViewingFRQGuide()) {
      return (
        <Text as='p' className='m-x0 text-center'>
          Free response questions must be assigned by your teacher. You can view your assignments
          from the classes tab.
        </Text>
      );
    }
    if (isStudent && belongsToLicensedClassrooms) {
      return (
        <>
          <Text as='p' className='text-center'>
            Ask your teacher to grant you independent practice access.
          </Text>
          <Text size='s' as='p' className='mt-x4 text-center' color='secondary'>
            If you purchased a subscription and have a question about refunds, please send an email
            to{' '}
            <Anchor
              underlined
              href='mailto:hello@albert.io?subject=Refund%20for%20locked%20questions'
            >
              hello@albert.io
            </Anchor>{' '}
            using the subject line &quot;Refund for locked questions&quot;.
          </Text>
        </>
      );
    }
    if (isStudent && isNotRetailSubject) {
      return (
        <Text as='p' className='text-center'>
          In order to complete this assessment, you must belong to a class. This is not available to
          individual subscribers.
        </Text>
      );
    }
    if (isTeacher) {
      return (
        <ul className='upgrade-subject-modal__selling-point-list'>
          {generateSellingPoint({
            content: ` Access to all subjects, for all students and teachers at your school`
          })}
          {generateSellingPoint({
            content: `The ability to assign any question in any subject`
          })}
          {generateSellingPoint({
            content: `Powerful data reports at the school, class and student level`
          })}
        </ul>
      );
    }
    return (
      <ul className='upgrade-subject-modal__selling-point-list'>
        {generateSellingPoint({
          total: questionsTotal,
          content: `Access to ${questionsTotal ? `${questionsTotal} more` : 'all'}  practice questions`
        })}
        {generateSellingPoint({
          total: freeResponseTotal,
          content: `Access to ${
            freeResponseTotal ? `${freeResponseTotal} more` : 'all'
          } free response examples`
        })}
        {generateSellingPoint({
          content: `Detailed explanations for every question`
        })}
        {generateSellingPoint({
          content: `Unlimited attempts per question`
        })}
        {generateSellingPoint({
          content: `Access to search results`
        })}
      </ul>
    );
  };

  const getMarketplaceLink = () =>
    `/marketplace/subjects?subject=${subject.getUrlSlug()}&subjectId=${subject.getId()}`;

  const generateButtonLink = () => {
    if (isTeacher || isNotRetailSubject) {
      return (
        <Button
          as='a'
          href='/pricing-schools'
          onClick={handleClose}
          className='upgrade-subject-modal__button'
        >
          Learn about licenses
        </Button>
      );
    }

    if (!isNotRetailSubject) {
      return (
        <Button
          as={Link}
          to={getMarketplaceLink()}
          onClick={handleClose}
          className='upgrade-subject-modal__button'
        >
          Upgrade subject
        </Button>
      );
    }

    return null;
  };

  const generateSubscriptionLink = () => {
    if (isNotRetailSubject) {
      return null;
    }
    return (
      <Button
        variant='text'
        as={Link}
        className='upgrade-subject-modal__button'
        to={getMarketplaceLink()}
        onClick={handleClose}
      >
        Purchase individual subscription
      </Button>
    );
  };

  const getImage = () => {
    let image = homeImage;
    if (isTeacher) {
      image = benefitsImage;
    }
    if (isStudent && isNotRetailSubject) {
      image = chalkboardImage;
    }
    return image;
  };

  // Dialogue's close-button props are a discriminated union, so the class's
  // `hideCloseBtn={!handleClose}` boolean has to be expressed as one branch or the other.
  const closeButtonProps = handleClose
    ? ({hideCloseBtn: false, handleClose} as const)
    : ({hideCloseBtn: true} as const);

  return (
    <Modal focusCloseButton={false} ariaLabel='Upgrade Subject Modal' handleClose={handleClose}>
      {({modalContentStyle}) => {
        return (
          <Dialogue
            inModal
            className={classnames([modalContentStyle, 'upgrade-subject-modal'])}
            {...closeButtonProps}
          >
            <Dialogue.Body>
              {!isLicensedClassroomsReady ? (
                <LoadingSpinner size={3} />
              ) : (
                <>
                  {generateCallToActionHeader()}
                  <img alt='' className='upgrade-subject-modal__img' src={getImage()} />
                  {generateSellingPoints()}
                  {!belongsToLicensedClassrooms && (
                    <ButtonGroup direction='column' align='center'>
                      {generateButtonLink()}
                      {generateSubscriptionLink()}
                    </ButtonGroup>
                  )}
                </>
              )}
            </Dialogue.Body>
          </Dialogue>
        );
      }}
    </Modal>
  );
}
